import { CACHE_TIERS } from "@workspace/types";
import { trpc } from "@/lib/trpc/react";

type ArtistSortOption =
	| "popularity"
	| "alpha"
	| "alpha-desc"
	| "name"
	| "name-desc"
	| "recent";

interface UseArtistsInfiniteParams {
	search?: string;
	genre?: string;
	sort?: ArtistSortOption;
	limit?: number;
}

export function useArtistsInfinite({
	search = "",
	genre = "all",
	sort = "popularity",
	limit = 24,
}: UseArtistsInfiniteParams = {}): ReturnType<
	typeof trpc.artist.list.useInfiniteQuery
> {
	// Map alpha/alpha-desc to name/name-desc for backward compatibility
	const mappedSort =
		sort === "alpha" ? "name" : sort === "alpha-desc" ? "name-desc" : sort;

	return trpc.artist.list.useInfiniteQuery(
		{
			search,
			genre,
			sort: mappedSort as "popularity" | "name" | "name-desc" | "recent",
			limit,
		},
		{
			getNextPageParam: (lastPage) => {
				if (!lastPage || !lastPage.hasNextPage) return undefined;
				return lastPage.nextCursor ?? undefined;
			},

			// Dynamic content - discovery lists
			...CACHE_TIERS.DYNAMIC,

			// Memory management - allow extensive artist discovery
			maxPages: 500,

			// Request deduplication - prevents duplicate requests
			structuralSharing: true,

			// Network optimization
			networkMode: "offlineFirst",

			// Improved error handling
			retry: (failureCount, error) => {
				// Don't retry on 4xx errors (client errors)
				if (error && typeof error === "object" && "status" in error) {
					const status = (error as any).status;
					if (status >= 400 && status < 500) {
						return false;
					}
				}
				// Retry up to 2 times for other errors
				return failureCount < 2;
			},
			retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),

			// Keep previous data while fetching
			placeholderData: (previousData) => previousData,

			// Only re-render on data/error changes, not isRefetching
			notifyOnChangeProps: ["data", "error", "isFetchingNextPage"],
		},
	);
}
